/** How long ago an instant was, for a column that is scanned rather than read.
 *
 * **Relative inside a week, a date after it.** "3 h ago" is what a list of things waiting for you
 * is scanned by; "41 days ago" is not, and a reader comparing it with a deadline has to do the
 * arithmetic, so past a week the date is written (`instantDate`, in the reader's zone, in words -
 * this product ships to two markets that read `03/10/2026` as two different days). The full stamp,
 * with its time and zone, is the caller's `title`, so the relative form never replaces the exact
 * one, it sits beside it.
 *
 * `now` is passed in so the function is a pure one: a test and a screen agree on what "now" is, and
 * nothing here reads a clock. An instant in the future (a skewed clock) or one that does not parse
 * is not "0 min ago": it falls through to the date, or to the string as it arrived.
 */

import type { Reader } from "./format";
import { instantDate } from "./format";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function relativeWhen(iso: string, now: number, reader?: Reader): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const elapsed = now - then;
  if (elapsed < 0) return instantDate(iso, reader);
  if (elapsed < MINUTE) return "just now";
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} min ago`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)} h ago`;
  if (elapsed < 2 * DAY) return "yesterday";
  if (elapsed < 7 * DAY) return `${Math.floor(elapsed / DAY)} days ago`;
  return instantDate(iso, reader);
}
